import React from 'react';
import { Alert, Form, Input, InputNumber, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { RouteOrderDto } from '../../../types/transport';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import { formatUnit } from '../../../utils/units';
import './TransportDeliveryQuantityFields.css';

const { Text } = Typography;

export interface TransportDeliveryCargoItem {
  orderId: string;
  orderProductId?: string;
  productName: string;
  quantity: number;
  unit?: string;
  deliveredQuantity?: number | null;
}

export interface TransportDeliveryFormItem {
  orderProductId: string;
  deliveredQuantity: number;
}

export function formatDeliveryQuantityInput(value?: string | number | null): string {
  const normalized = String(value ?? '').replace('.', ',');
  if (!normalized.includes(',')) return normalized;
  return normalized.replace(/0+$/, '').replace(/,$/, '');
}

export function buildTransportDeliveryFormItems(
  cargoItems: TransportDeliveryCargoItem[] = [],
): TransportDeliveryFormItem[] {
  return cargoItems
    .filter((item): item is TransportDeliveryCargoItem & { orderProductId: string } => Boolean(item.orderProductId))
    .map((item) => ({
      orderProductId: item.orderProductId,
      deliveredQuantity: item.deliveredQuantity == null
        ? Number(item.quantity || 0)
        : Number(item.deliveredQuantity),
    }));
}

interface Props {
  cargoItems: TransportDeliveryCargoItem[];
  routeOrders?: RouteOrderDto[];
  name?: string;
}

const TransportDeliveryQuantityFields: React.FC<Props> = ({
  cargoItems = [],
  routeOrders = [],
  name = 'deliveryItems',
}) => {
  const { t, i18n } = useTranslation(['transport']);
  const orderNumberById = new Map(routeOrders.map((routeOrder) => [
    routeOrder.orderId,
    routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber,
  ]));
  const missingIdentity = cargoItems.some((item) => !item.orderProductId);

  return (
    <section className="transport-delivery-quantity-fields">
      <div className="transport-delivery-quantity-fields__intro">
        <strong>{t('transport:deliveryConfirmation.title')}</strong>
        <Text type="secondary">{t('transport:deliveryConfirmation.description')}</Text>
      </div>

      {routeOrders.length > 1 ? (
        <aside className="transport-delivery-quantity-fields__shared">
          <WarningOutlined />
          <div>
            <strong>{t('transport:deliveryConfirmation.sharedTitle')}</strong>
            <span>{t('transport:deliveryConfirmation.sharedDescription', {
              orders: routeOrders
                .map((routeOrder) => routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber)
                .filter(Boolean)
                .join(', '),
            })}</span>
          </div>
        </aside>
      ) : null}

      {missingIdentity ? (
        <Alert
          type="error"
          showIcon
          message={t('transport:deliveryConfirmation.missingProductLink')}
        />
      ) : null}

      <Form.List name={name}>
        {(fields) => (
          <div className="transport-delivery-quantity-fields__list">
            {fields.map((field, index) => {
              const cargo = cargoItems[index];
              if (!cargo?.orderProductId) return null;
              const plannedQuantity = Number(cargo.quantity || 0);
              const unit = formatUnit(cargo.unit, i18n.language);
              const orderNumber = orderNumberById.get(cargo.orderId);

              return (
                <div className="transport-delivery-quantity-fields__row" key={cargo.orderProductId}>
                  <div className="transport-delivery-quantity-fields__product">
                    <strong>{cargo.productName}</strong>
                    <div className="transport-delivery-quantity-fields__meta">
                      {orderNumber ? <span>{orderNumber}</span> : null}
                      <Text type="secondary">{t('transport:deliveryConfirmation.planned', {
                        quantity: formatLocalizedQuantity(plannedQuantity, i18n.language),
                        unit,
                      })}</Text>
                    </div>
                  </div>
                  <Form.Item name={[field.name, 'orderProductId']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    className="transport-delivery-quantity-fields__input"
                    name={[field.name, 'deliveredQuantity']}
                    label={t('transport:deliveryConfirmation.actual')}
                    rules={[
                      { required: true, message: t('transport:deliveryConfirmation.required') },
                      {
                        validator: async (_, value) => {
                          const quantity = Number(value);
                          if (
                            !Number.isFinite(quantity)
                            || quantity < 0
                          ) {
                            throw new Error(t('transport:deliveryConfirmation.range'));
                          }
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      precision={3}
                      step={0.001}
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                      formatter={(value, info) => (
                        info.userTyping ? info.input : formatDeliveryQuantityInput(value)
                      )}
                      addonAfter={unit}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const deliveredQuantity = Number(
                        getFieldValue([name, field.name, 'deliveredQuantity']),
                      );
                      if (!Number.isFinite(deliveredQuantity) || deliveredQuantity <= plannedQuantity) {
                        return null;
                      }
                      return (
                        <Alert
                          className="transport-delivery-quantity-fields__over-plan"
                          type="warning"
                          showIcon
                          message={t('transport:deliveryConfirmation.overPlan', {
                            difference: formatLocalizedQuantity(
                              deliveredQuantity - plannedQuantity,
                              i18n.language,
                            ),
                            unit,
                          })}
                        />
                      );
                    }}
                  </Form.Item>
                </div>
              );
            })}
          </div>
        )}
      </Form.List>
    </section>
  );
};

export default TransportDeliveryQuantityFields;
